import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MapContainer, TileLayer, Marker, Circle } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useSeniorLocation } from '../../hooks/useSeniorLocation';

// react-leaflet's default marker icon URLs break under webpack; the standard
// fix is to re-bind the asset paths to the CDN copies.
delete (L.Icon.Default.prototype as { _getIconUrl?: unknown })._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl:       'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl:     'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

interface Props {
  seniorId: string;
  seniorName: string;
  /** From assessments.profile_type / seniors.profile_family. Cognitive triggers
   *  the warning banner per the F03 brief. */
  profileType?: 'cognitive' | 'physical' | 'visual' | string | null;
  /**
   * D23 fix — is the PHONE reachable right now (Firebase heartbeat, which is
   * independent of location)? Without this the panel can only see how old the
   * last position is, so it blamed a stale position on the phone being off even
   * when the phone is plainly online and the senior had simply revoked GPS
   * sharing — a shipped feature. `undefined` = unknown; falls back to the old
   * wording rather than guessing.
   */
  isOnline?: boolean;
}

const STALE_THRESHOLD_MS = 30 * 60 * 1000;  // > 30 min = "phone may be off"

function minutesAgo(updatedAt: string): number {
  return Math.max(0, Math.floor((Date.now() - new Date(updatedAt).getTime()) / 60_000));
}

export const LocationPanel: React.FC<Props> = ({ seniorId, seniorName, profileType, isOnline }) => {
  const { t } = useTranslation();
  const { location, loading } = useSeniorLocation(seniorId);
  const isCognitive = profileType === 'cognitive';
  const [warningExpanded, setWarningExpanded] = useState(false);

  const status = (() => {
    if (loading) return { kind: 'loading' as const };
    if (!location) return { kind: 'unavailable' as const };
    const age = Date.now() - new Date(location.updated_at).getTime();
    if (age > STALE_THRESHOLD_MS) {
      // The phone is reachable, so "phone may be off" is provably wrong here —
      // the position is old because sharing is off or not updating.
      if (isOnline === true) return { kind: 'notSharing' as const, mins: Math.floor(age / 60_000) };
      return { kind: 'stale' as const, mins: Math.floor(age / 60_000) };
    }
    return { kind: 'fresh' as const, mins: minutesAgo(location.updated_at) };
  })();

  return (
    // `relative isolate` caps Leaflet's internal z-index 1000 controls
    // (zoom buttons, attribution) inside this section's stacking context.
    // Without it, those controls bleed above modal/drawer backdrops that
    // sit at z-50, breaking the layering of anything that opens after the
    // map mounts.
    <section className="relative isolate bg-white border border-brand-border/70 rounded-2xl p-5 mt-3">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="material-symbols-outlined text-brand-primary" style={{ fontSize: '22px' }}>
            location_on
          </span>
          <h3 className="text-base font-semibold text-gray-900">
            {t('location.title', { defaultValue: `${seniorName}'s location` })}
          </h3>
        </div>
        {status.kind === 'fresh' && (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-green-50 text-green-700 text-xs font-medium">
            <span className="material-symbols-outlined" style={{ fontSize: '14px' }}>schedule</span>
            {t('location.updatedAgo', { mins: status.mins, defaultValue: `Updated ${status.mins} min ago` })}
          </span>
        )}
        {status.kind === 'notSharing' && (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-50 text-amber-700 text-xs font-medium">
            <span className="material-symbols-outlined" style={{ fontSize: '14px' }}>location_off</span>
            {t('location.notSharing')}
          </span>
        )}
        {status.kind === 'stale' && (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-50 text-amber-700 text-xs font-medium">
            <span className="material-symbols-outlined" style={{ fontSize: '14px' }}>warning</span>
            {t('location.stale', { defaultValue: 'Location stale — phone may be off' })}
          </span>
        )}
      </div>

      {/* F03 brief: cognitive-profile warning banner above the map. Short
          title with (?) tooltip — extended explanation lives behind the
          expand toggle so the warning doesn't dominate the panel.
          Informational, not blocking — caregiver can still view location. */}
      {isCognitive && (
        <div className="mb-3 p-3 rounded-xl bg-amber-50 border border-amber-200">
          <div className="flex gap-2 items-start">
            <span className="material-symbols-outlined text-amber-600 flex-shrink-0" style={{ fontSize: '20px' }}>
              warning
            </span>
            <div className="text-xs text-amber-900 leading-relaxed flex-1">
              <div className="flex items-center gap-1.5 mb-0.5">
                <strong>
                  {t('location.cognitiveWarning.title', { defaultValue: 'Note before enabling' })}
                </strong>
                <button
                  type="button"
                  onClick={() => setWarningExpanded((v) => !v)}
                  aria-expanded={warningExpanded}
                  aria-label={t('location.cognitiveWarning.tooltipLabel', { defaultValue: 'Read full explanation' })}
                  // 20px badge, expanded to a ~44px hit area via the pseudo-
                  // element so the touch target meets the minimum without
                  // enlarging the badge inside the warning banner.
                  className="relative inline-flex items-center justify-center w-5 h-5 rounded-full bg-amber-100 hover:bg-amber-200 transition-colors text-amber-900 font-bold text-xs leading-none border border-amber-300 before:absolute before:-inset-3 before:content-['']"
                >
                  ?
                </button>
              </div>
              {warningExpanded && (
                <div className="mt-1">
                  {t('location.cognitiveWarning.body', {
                    defaultValue: 'People with memory loss often leave their phone behind when they walk out. Enable this if your parent typically carries their phone with them — it could save their life. Otherwise, this feature won’t help in a wandering event.',
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {status.kind === 'loading' && (
        <div className="h-64 bg-gray-50 rounded-xl flex items-center justify-center text-sm text-gray-500">
          {t('location.loading', { defaultValue: 'Loading map…' })}
        </div>
      )}

      {status.kind === 'unavailable' && (
        <div className="h-64 bg-gray-50 rounded-xl flex flex-col items-center justify-center text-sm text-gray-500 gap-2 p-4 text-center">
          <span className="material-symbols-outlined text-gray-400" style={{ fontSize: '32px' }}>
            location_off
          </span>
          <div>
            {t('location.unavailable', {
              defaultValue: 'Location unavailable — phone may be off, or location sharing is not enabled on the senior’s phone.',
            })}
          </div>
        </div>
      )}

      {(status.kind === 'fresh' || status.kind === 'stale' || status.kind === 'notSharing') && location && (
        <div className="h-64 rounded-xl overflow-hidden">
          <MapContainer
            center={[location.lat, location.lon]}
            zoom={15}
            style={{ height: '100%', width: '100%' }}
            scrollWheelZoom={false}
            key={`${location.lat}-${location.lon}-${location.updated_at}`}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <Marker position={[location.lat, location.lon]} />
            {location.accuracy_meters > 0 && (
              <Circle
                center={[location.lat, location.lon]}
                radius={location.accuracy_meters}
                pathOptions={{ color: '#1E5F8A', fillColor: '#1E5F8A', fillOpacity: 0.12, weight: 1 }}
              />
            )}
          </MapContainer>
        </div>
      )}
    </section>
  );
};
